import {
  CardInstance,
  CardPosition,
  MatchState,
  Seat,
  otherSeat,
} from '../types/state';
import { CardKind } from '../types/cards';
import {
  CardLibrary,
  effectiveStatBonus,
  findCard,
  getCardData,
  getLevel,
  getModifiedAtk,
  getModifiedDef,
  getMonsters,
  getSpellTraps,
  kindOf,
} from './helpers';
import {
  isTwoProngedTradeFavorable,
  STARTER_IDS,
  twoProngedTradeForAi,
} from './effects';
import { plannerDifficulty, type AiDeckDifficulty } from './aiDeck';
import { EXODIA_PIECE_SET } from './aiRecipes';

const TRAP_HOLE_ID = 4206964;
/** Hard+ spends Trap Hole on La Jinn and tributes, not on a 1400. */
const TRAP_HOLE_PREMIUM_ATK = 1800;
/** Normal still holes the 1700 bait line. */
const TRAP_HOLE_NORMAL_ATK = 1700;
const WABOKU_LP_FLOOR = 3000;
const BEATER_ATK = 1700;
const TWO_PRONGED_ID = 83887306;
const DRAGON_CAPTURE_JAR_ID = 50045299;

/**
 * Higher wins when the AI picks among several legal chain cards. Destroying
 * the problem beats locking it, which beats soaking damage, which beats a boost.
 */
const MIRROR_FORCE_ID = 44095762;
const SOLEMN_ID = 41420027;
const MAGIC_JAMMER_ID = 77414722;
const SEVEN_TOOLS_ID = 3819470;
const HORN_OF_HEAVEN_ID = 98069388;
const FAKE_TRAP_ID = 3027001;
const SANGAN_ID = 26202165;
const WITCH_ID = 78010363;
const HEAVY_STORM_ID = 19613556;

/** Spells worth a Solemn or a Magic Jammer. Fissure is not on this list. */
const POWER_SPELLS = new Set([
  55144522, // Pot of Greed
  12580477, // Raigeki
  53129443, // Dark Hole
  HEAVY_STORM_ID,
  4031928, // Change of Heart
  83764718, // Monster Reborn
  72302403, // Swords of Revealing Light
  79759861, // Tribute to the Doomed
]);

const LAST_COPY = new Set([
  '55144522',
  '12580477',
  '53129443',
  '83764718',
  '4031928',
  '44095762',
  '26202165',
  '78010363',
  '40640057',
  '31560081',
]);

const AI_CHAIN_RANK: Record<number, number> = {
  [SOLEMN_ID]: 120,
  [MAGIC_JAMMER_ID]: 115,
  [MIRROR_FORCE_ID]: 110,
  [SEVEN_TOOLS_ID]: 105,
  [TRAP_HOLE_ID]: 100,
  [FAKE_TRAP_ID]: 98,
  [HORN_OF_HEAVEN_ID]: 95,
  [TWO_PRONGED_ID]: 90,
  [DRAGON_CAPTURE_JAR_ID]: 80,
  [STARTER_IDS.WABOKU]: 70,
  [STARTER_IDS.JUST_DESSERTS]: 50,
  [STARTER_IDS.REINFORCEMENTS]: 40,
  [STARTER_IDS.CASTLE_WALLS]: 40,
  [STARTER_IDS.REVERSE_TRAP]: 20,
};

const BOOST_ATK = 500;
const BOOST_DEF = 500;
const DESSERTS_PER = 500;

export interface AiChainChoice {
  uid: string;
  targets?: string[];
}

/**
 * What the links already on the chain will have done once it resolves. The AI
 * gets priority again after its own link, so without this it would answer the
 * same summon or the same battle twice.
 */
interface PendingChain {
  /** Monsters a link is already set to destroy. */
  doomed: Set<string>;
  /** ATK a pending Reinforcements will add, per monster uid. */
  atkBoost: Map<string, number>;
  /** DEF a pending Castle Walls will add, per monster uid. */
  defBoost: Map<string, number>;
  /** Our Waboku is waiting to resolve. */
  waboku: boolean;
  /** A Reverse Trap is waiting to resolve (a second one does nothing). */
  reverse: boolean;
  /** A Dragon Capture Jar is waiting to resolve. */
  jar: boolean;
  /** Damage already queued at the opponent. */
  burn: number;
}

function readPendingChain(state: MatchState, responder: Seat): PendingChain {
  const pending: PendingChain = {
    doomed: new Set(),
    atkBoost: new Map(),
    defBoost: new Map(),
    waboku: false,
    reverse: false,
    jar: false,
    burn: 0,
  };
  const oppMonsters = getMonsters(state.zones[otherSeat(responder)]).length;
  for (const link of state.chain) {
    const first = link.targetUids[0];
    switch (Number(link.cardId)) {
      case TRAP_HOLE_ID: {
        const victim = first ?? link.triggerUid;
        if (victim) pending.doomed.add(victim);
        break;
      }
      case TWO_PRONGED_ID:
        for (const target of link.targetUids) pending.doomed.add(target);
        break;
      case DRAGON_CAPTURE_JAR_ID:
        pending.jar = true;
        break;
      case STARTER_IDS.WABOKU:
        if (link.controller === responder) pending.waboku = true;
        break;
      case STARTER_IDS.REVERSE_TRAP:
        pending.reverse = true;
        break;
      case STARTER_IDS.JUST_DESSERTS:
        if (link.controller === responder) {
          pending.burn += DESSERTS_PER * oppMonsters;
        }
        break;
      case STARTER_IDS.REINFORCEMENTS:
        if (first) addBoost(pending.atkBoost, first, BOOST_ATK);
        break;
      case STARTER_IDS.CASTLE_WALLS:
        if (first) addBoost(pending.defBoost, first, BOOST_DEF);
        break;
      default:
        break;
    }
  }
  return pending;
}

function addBoost(boosts: Map<string, number>, uid: string, amount: number): void {
  boosts.set(uid, (boosts.get(uid) ?? 0) + amount);
}

/** Waboku (resolved, or waiting on the chain) already saves our side. */
function battleCovered(
  state: MatchState,
  responder: Seat,
  pending: PendingChain,
): boolean {
  if (pending.waboku) return true;
  return state.battleDamageImmune[responder] && state.battleDestroyImmune[responder];
}

interface BattleResult {
  attackerDestroyed: boolean;
  defenderDestroyed: boolean;
  damageToAttacker: number;
  damageToDefender: number;
}

/**
 * Situational chain answers for an AI seat. Empty means pass. Callers still
 * validate each activation; this only ranks and filters.
 */
export function planAiChainResponses(
  state: MatchState,
  library: CardLibrary,
  responder: Seat,
  activatableUids: string[],
): AiChainChoice[] {
  const set = getSpellTraps(state.zones[responder]);
  const byUid = new Map(set.map((c) => [c.uid, c]));
  const hasCastleWalls = activatableUids.some(
    (uid) => Number(byUid.get(uid)?.cardId) === STARTER_IDS.CASTLE_WALLS,
  );
  const pending = readPendingChain(state, responder);
  const scored: { choice: AiChainChoice; rank: number }[] = [];
  for (const uid of activatableUids) {
    const card = byUid.get(uid);
    if (!card) continue;
    const id = Number(card.cardId);
    if (id === STARTER_IDS.ULTIMATE_OFFERING) continue;
    const choice = planOneCard(
      state, library, responder, uid, id, hasCastleWalls, pending,
    );
    if (!choice) continue;
    scored.push({ choice, rank: AI_CHAIN_RANK[id] ?? 1 });
  }
  scored.sort((a, b) => b.rank - a.rank);
  return scored.map((s) => s.choice);
}

function planOneCard(
  state: MatchState,
  library: CardLibrary,
  responder: Seat,
  uid: string,
  id: number,
  hasCastleWalls: boolean,
  pending: PendingChain,
): AiChainChoice | null {
  switch (id) {
    case STARTER_IDS.CASTLE_WALLS:
      return planCastleWalls(state, library, responder, uid, pending);
    case STARTER_IDS.REINFORCEMENTS:
      return planReinforcements(state, library, responder, uid, pending);
    case STARTER_IDS.WABOKU:
      return planWaboku(state, library, responder, hasCastleWalls, pending, uid)
        ? { uid }
        : null;
    case STARTER_IDS.REVERSE_TRAP:
      return planReverseTrap(state, library, responder, pending) ? { uid } : null;
    case STARTER_IDS.JUST_DESSERTS:
      return planJustDesserts(state, responder, pending) ? { uid } : null;
    case TRAP_HOLE_ID: {
      // A link on the chain already destroys the summon: a second copy would
      // just fizzle once the first one resolves.
      const victim = state.chainTriggerUid;
      if (victim && pending.doomed.has(victim)) return null;
      if (!shouldActivateTrapHole(state, library, responder)) return null;
      return { uid };
    }
    case DRAGON_CAPTURE_JAR_ID:
      return planDragonCaptureJar(state, library, responder, pending)
        ? { uid }
        : null;
    case TWO_PRONGED_ID: {
      if (!isTwoProngedTradeFavorable(state, library, responder)) return null;
      const targets = twoProngedTradeForAi(state, library, responder);
      if (!targets) return null;
      // Paying two of our bodies for a monster the chain already kills, and
      // fizzling that link on the way, is the worst trade on the board.
      if (targets.some((target) => pending.doomed.has(target))) return null;
      return { uid, targets };
    }
    case MIRROR_FORCE_ID:
      return planMirrorForce(state, library, responder, uid, pending);
    case SOLEMN_ID:
      return planSolemn(state, library, responder, uid);
    case MAGIC_JAMMER_ID:
      return planMagicJammer(state, library, responder, uid);
    case SEVEN_TOOLS_ID:
      return planSevenTools(state, library, responder, uid);
    case HORN_OF_HEAVEN_ID:
      return planHorn(state, library, responder, uid);
    case FAKE_TRAP_ID:
      return { uid };
    default:
      return null;
  }
}

/**
 * Kuriboh only zeroes the damage of this attack. Same Life Point line as
 * Waboku: Easy spends it on any real hit, harder levels save it.
 */
export function shouldDropKuriboh(
  state: MatchState,
  library: CardLibrary,
  responder: Seat,
): boolean {
  const pending = readPendingChain(state, responder);
  if (battleCovered(state, responder, pending)) return false;
  const fight = currentFight(state, library, responder, pending);
  if (!fight || fight.aiIsAttacker) return false;
  let damage = 0;
  if (fight.direct) damage = fight.attackerAtk;
  else if (fight.defender && fight.defenderPublic && !fight.defending) {
    damage = simulateBattle(
      fight.attackerAtk, fight.defenderValue, false,
    ).damageToDefender;
  }
  if (damage <= 0) return false;
  const level = plannerDifficulty(state, responder);
  if (level === 'easy') return true;
  return wabokuForLife(state.zones[responder].lifePoints, damage, level);
}

/** Discard fodder that is not an Exodia piece or the last copy of a staple. */
export function aiLowValueDiscard(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
): string | null {
  const hand = state.zones[seat].hand;
  const counts = new Map<string, number>();
  for (const card of hand) counts.set(card.cardId, (counts.get(card.cardId) ?? 0) + 1);
  const candidates = hand.filter((card) => {
    if (EXODIA_PIECE_SET.has(card.cardId)) return false;
    if (LAST_COPY.has(card.cardId) && (counts.get(card.cardId) ?? 0) <= 1) return false;
    return true;
  });
  if (candidates.length === 0) return null;
  const level = plannerDifficulty(state, seat);
  const value = (card: CardInstance): number => {
    // Discarding Thunder Dragon is the search, when a copy is still in the Deck.
    if (
      level !== 'easy' &&
      level !== 'test' &&
      Number(card.cardId) === 31786629 &&
      state.zones[seat].deck.some((copy) => Number(copy.cardId) === 31786629)
    ) {
      return 0;
    }
    const data = getCardData(library, card.cardId);
    if (kindOf(card, library) === CardKind.MONSTER) {
      return (data.atk ?? 0) + getLevel(card, library) * 40;
    }
    if (LAST_COPY.has(card.cardId)) return 4000;
    return 700;
  };
  candidates.sort((a, b) => value(a) - value(b));
  return candidates[0]?.uid ?? null;
}

/** Sangan or Witch first, then the weakest monster. */
export function aiPreferRecruiterTribute(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
): string | null {
  const mine = getMonsters(state.zones[seat]);
  if (mine.length === 0) return null;
  const recruiter = mine.find((monster) => {
    const id = Number(monster.cardId);
    return id === SANGAN_ID || id === WITCH_ID;
  });
  if (recruiter) return recruiter.uid;
  return mine.reduce((best, monster) =>
    getModifiedAtk(monster, library, state.reverseStatMods) <
    getModifiedAtk(best, library, state.reverseStatMods)
      ? monster
      : best,
  ).uid;
}

/**
 * The Jar locks Dragons. Flipping it with none to lock only reveals it, and the
 * lock does not stack, so one face-up copy is enough.
 */
function planDragonCaptureJar(
  state: MatchState,
  library: CardLibrary,
  responder: Seat,
  pending: PendingChain,
): boolean {
  if (pending.jar) return false;
  const ours = getSpellTraps(state.zones[responder]);
  if (ours.some((c) => c.faceUp && Number(c.cardId) === DRAGON_CAPTURE_JAR_ID)) {
    return false;
  }
  return getMonsters(state.zones[otherSeat(responder)]).some(
    (m) =>
      m.faceUp &&
      !pending.doomed.has(m.uid) &&
      getCardData(library, m.cardId).race === 'Dragon',
  );
}

function planCastleWalls(
  state: MatchState,
  library: CardLibrary,
  responder: Seat,
  uid: string,
  pending: PendingChain,
): AiChainChoice | null {
  if (state.reverseStatMods) return null;
  const fight = currentFight(state, library, responder, pending);
  if (!fight || fight.direct || !fight.defender) return null;
  if (fight.aiIsAttacker) return null;
  if (!fight.defenderFaceUp) return null;
  if (fight.defender.position !== CardPosition.DEFENSE) return null;
  if (pending.doomed.has(fight.defender.uid)) return null;
  if (boostBlocked(state, library, fight.defender.uid)) return null;
  const atk = fight.attackerAtk;
  const def = fight.defenderValue;
  const newDef = boostedStat(
    fight.defender,
    library,
    state,
    0,
    BOOST_DEF + (pending.defBoost.get(fight.defender.uid) ?? 0),
  );
  const alreadyLethal =
    def > atk && def - atk >= state.zones[fight.attackSeat].lifePoints;
  const bounce = Math.max(0, newDef - atk);
  const lethalBounce =
    newDef > atk && bounce >= state.zones[fight.attackSeat].lifePoints;
  // Waboku already keeps the wall alive; only the bigger bounce is left to win.
  const saves =
    !battleCovered(state, responder, pending) && atk > def && atk <= newDef;
  const extraLethal = lethalBounce && !alreadyLethal;
  if (!saves && !extraLethal) return null;
  return { uid, targets: [fight.defender.uid] };
}

function planReinforcements(
  state: MatchState,
  library: CardLibrary,
  responder: Seat,
  uid: string,
  pending: PendingChain,
): AiChainChoice | null {
  if (state.reverseStatMods) return null;
  const fight = currentFight(state, library, responder, pending);
  if (!fight || fight.direct) return null;
  const covered = battleCovered(state, responder, pending);
  if (fight.aiIsAttacker) {
    if (!fight.defenderPublic || !fight.defender) return null;
    if (!fight.attacker.faceUp) return null;
    if (pending.doomed.has(fight.attacker.uid)) return null;
    if (boostBlocked(state, library, fight.attacker.uid)) return null;
    const now = simulateBattle(
      fight.attackerAtk, fight.defenderValue, fight.defending,
    );
    const boosted = simulateBattle(
      boostedStat(
        fight.attacker,
        library,
        state,
        BOOST_ATK + (pending.atkBoost.get(fight.attacker.uid) ?? 0),
        0,
      ),
      fight.defenderValue,
      fight.defending,
    );
    if (aiScore(now, true, covered) >= aiScore(boosted, true, covered)) return null;
    return { uid, targets: [fight.attacker.uid] };
  }
  if (!fight.defender || !fight.defenderFaceUp) return null;
  if (fight.defender.position !== CardPosition.ATTACK) return null;
  if (pending.doomed.has(fight.defender.uid)) return null;
  if (boostBlocked(state, library, fight.defender.uid)) return null;
  const now = simulateBattle(
    fight.attackerAtk, fight.defenderValue, false,
  );
  const boosted = simulateBattle(
    fight.attackerAtk,
    boostedStat(
      fight.defender,
      library,
      state,
      BOOST_ATK + (pending.atkBoost.get(fight.defender.uid) ?? 0),
      0,
    ),
    false,
  );
  if (aiScore(now, false, covered) >= aiScore(boosted, false, covered)) return null;
  return { uid, targets: [fight.defender.uid] };
}

function planWaboku(
  state: MatchState,
  library: CardLibrary,
  responder: Seat,
  hasCastleWalls: boolean,
  pending: PendingChain,
  uid: string,
): boolean {
  if (battleCovered(state, responder, pending)) return false;
  // The card is destroyed either way. Chaining keeps the protection through
  // the opponent's Battle Phase. On our turn that protection ends first.
  if (wabokuSavedFromDestruction(state, responder, uid)) return true;
  const fight = currentFight(state, library, responder, pending);
  if (!fight) return false;
  if (fight.aiIsAttacker) return false;
  const level = plannerDifficulty(state, responder);
  const lp = state.zones[responder].lifePoints;
  // Easy still spends Waboku on any real hit. Normal+ keeps it for advantage.
  if (fight.direct) {
    if (level === 'easy') return true;
    return wabokuForLife(lp, fight.attackerAtk, level);
  }
  if (!fight.defender || !fight.defenderPublic) return false;
  const result = simulateBattle(
    fight.attackerAtk, fight.defenderValue, fight.defending,
  );
  const weDie = result.defenderDestroyed;
  const dmgToUs = result.damageToDefender;
  if (!weDie && dmgToUs <= 0) return false;
  if (
    hasCastleWalls &&
    fight.defending &&
    castleWallsWouldCover(state, library, fight, pending)
  ) {
    return false;
  }
  if (level === 'easy') return true;
  // Equal ATK: ours lives and theirs still dies.
  const equalAtk = !fight.defending && fight.attackerAtk === fight.defenderValue;
  if (equalAtk && weDie) return true;
  if (weDie) {
    const ourAtk = getModifiedAtk(fight.defender, library, state.reverseStatMods);
    const onlyMonster = getMonsters(state.zones[responder]).length === 1;
    if (ourAtk >= BEATER_ATK || onlyMonster) return true;
  }
  return wabokuForLife(lp, dmgToUs, level);
}

/**
 * Opponent's turn, and the previous link would destroy this Waboku.
 * Either side having a monster means a battle can still happen this turn.
 */
function wabokuSavedFromDestruction(state: MatchState, responder: Seat, uid: string): boolean {
  if (state.turnPlayer === responder) return false;
  const link = state.chain[state.chain.length - 1];
  if (!link || !(link.destroyUids ?? []).includes(uid)) return false;
  return ([0, 1] as Seat[]).some((seat) => getMonsters(state.zones[seat]).length > 0);
}

/** LP-only hits. A 500 poke at 8000 is not worth the card. */
function wabokuForLife(lp: number, damage: number, level: AiDeckDifficulty): boolean {
  if (damage <= 0) return false;
  if (damage >= lp) return true;
  const line = level === 'normal' ? 1000 : 2000;
  if (damage >= line) return true;
  if (lp - damage <= WABOKU_LP_FLOOR) return true;
  return false;
}

function planMirrorForce(
  state: MatchState,
  library: CardLibrary,
  responder: Seat,
  uid: string,
  pending: PendingChain,
): AiChainChoice | null {
  const attack = state.pendingAttack;
  if (!attack || attack.seat === responder) return null;
  const attackers = getMonsters(state.zones[attack.seat]).filter(
    (monster) => monster.faceUp && monster.position === CardPosition.ATTACK,
  );
  if (attackers.length === 0) return null;
  const threats = attackers.filter((monster) => {
    const id = Number(monster.cardId);
    return id !== SANGAN_ID && id !== WITCH_ID;
  });
  const level = plannerDifficulty(state, responder);
  if (threats.length === 0) {
    if (level === 'easy' && attackers.length >= 2) return { uid };
    return null;
  }
  if (threats.length >= 2 || level === 'easy') return { uid };
  const atk = getModifiedAtk(threats[0]!, library, state.reverseStatMods);
  if (atk >= TRAP_HOLE_PREMIUM_ATK) return { uid };
  const fight = currentFight(state, library, responder, pending);
  if (!fight || fight.aiIsAttacker) return null;
  const lp = state.zones[responder].lifePoints;
  if (fight.direct && (fight.attackerAtk >= 1500 || fight.attackerAtk >= lp)) return { uid };
  if (!fight.direct && fight.defenderPublic) {
    const result = simulateBattle(fight.attackerAtk, fight.defenderValue, fight.defending);
    if (result.damageToDefender >= 1500 || result.damageToDefender >= lp) return { uid };
  }
  return null;
}

function solemnLifeOk(lp: number, level: AiDeckDifficulty, serious: boolean): boolean {
  if (lp < 1) return false;
  if (level === 'easy' || level === 'normal') return lp > 500;
  if (serious) return true;
  return lp >= 2000;
}

function planSolemn(
  state: MatchState,
  library: CardLibrary,
  responder: Seat,
  uid: string,
): AiChainChoice | null {
  const lp = state.zones[responder].lifePoints;
  const level = plannerDifficulty(state, responder);
  const last = state.chain[state.chain.length - 1];
  if (last && last.controller !== responder) {
    const found = findCard(state, last.sourceUid);
    const id = Number(last.cardId);
    const kind = found ? kindOf(found.instance, library) : null;
    if (kind === CardKind.SPELL) {
      const power = POWER_SPELLS.has(id);
      if (!power && level !== 'easy') return null;
      if (level === 'easy' && !power && id === 66788016) return null;
      if (!solemnLifeOk(lp, level, power)) return null;
      return { uid };
    }
    if (kind === CardKind.TRAP) {
      const wipe = id === MIRROR_FORCE_ID || id === SOLEMN_ID;
      if (!wipe && level !== 'easy') return null;
      if (!solemnLifeOk(lp, level, wipe)) return null;
      return { uid };
    }
  }
  const trigger = state.chainTriggerUid;
  if (!trigger) return null;
  const found = findCard(state, trigger);
  if (!found || found.location.zone !== 'monsters') {
    if (level !== 'easy') return null;
    return solemnLifeOk(lp, level, false) ? { uid } : null;
  }
  const atk = found.instance.faceUp
    ? getModifiedAtk(found.instance, library, state.reverseStatMods)
    : 0;
  const stars = getLevel(found.instance, library);
  const serious = stars >= 7 || atk >= 2400;
  const big = stars >= 5 || atk >= (level === 'easy' ? 1000 : TRAP_HOLE_PREMIUM_ATK);
  if (!big) return null;
  if (!solemnLifeOk(lp, level, serious)) return null;
  return { uid };
}

function planMagicJammer(
  state: MatchState,
  library: CardLibrary,
  responder: Seat,
  uid: string,
): AiChainChoice | null {
  const last = state.chain[state.chain.length - 1];
  if (!last || last.controller === responder) return null;
  const found = findCard(state, last.sourceUid);
  if (!found || kindOf(found.instance, library) !== CardKind.SPELL) return null;
  const id = Number(last.cardId);
  const level = plannerDifficulty(state, responder);
  if (!POWER_SPELLS.has(id) && level !== 'easy') return null;
  const discard = aiLowValueDiscard(state, library, responder);
  if (!discard) return null;
  return { uid, targets: [discard] };
}

function planSevenTools(
  state: MatchState,
  library: CardLibrary,
  responder: Seat,
  uid: string,
): AiChainChoice | null {
  if (state.zones[responder].lifePoints < 1000) return null;
  const last = state.chain[state.chain.length - 1];
  if (!last || last.controller === responder) return null;
  const found = findCard(state, last.sourceUid);
  if (!found || kindOf(found.instance, library) !== CardKind.TRAP) return null;
  const id = Number(last.cardId);
  const level = plannerDifficulty(state, responder);
  if (id === MIRROR_FORCE_ID || id === SOLEMN_ID || id === HORN_OF_HEAVEN_ID) return { uid };
  if (id === TRAP_HOLE_ID) {
    const victim = state.chainTriggerUid;
    const monster = victim ? findCard(state, victim) : null;
    if (!monster || monster.location.seat !== responder) return null;
    const atk = getModifiedAtk(monster.instance, library, state.reverseStatMods);
    if (atk >= BEATER_ATK || getLevel(monster.instance, library) >= 5) return { uid };
    return null;
  }
  if (id === STARTER_IDS.WABOKU) {
    const fight = currentFight(state, library, responder, readPendingChain(state, responder));
    if (!fight?.aiIsAttacker) return null;
    if (fight.direct && fight.attackerAtk >= 2000) return { uid };
    return null;
  }
  return level === 'easy' ? { uid } : null;
}

function planHorn(
  state: MatchState,
  library: CardLibrary,
  responder: Seat,
  uid: string,
): AiChainChoice | null {
  const trigger = state.chainTriggerUid;
  if (!trigger) return null;
  const found = findCard(state, trigger);
  const level = plannerDifficulty(state, responder);
  if (found && found.location.zone === 'monsters' && found.instance.faceUp) {
    const atk = getModifiedAtk(found.instance, library, state.reverseStatMods);
    const stars = getLevel(found.instance, library);
    const line = level === 'easy' ? 1400 : TRAP_HOLE_PREMIUM_ATK;
    if (atk < line && stars < 5) return null;
  } else if (level !== 'easy') {
    return null;
  }
  const tribute = aiPreferRecruiterTribute(state, library, responder);
  if (!tribute) return null;
  return { uid, targets: [tribute] };
}

/**
 * Easy holes every legal summon. Hard+ saves the copy for 1800+ or a tribute,
 * and spends it on a smaller body only when that body actually beats us.
 */
function shouldActivateTrapHole(
  state: MatchState,
  library: CardLibrary,
  responder: Seat,
): boolean {
  const level = plannerDifficulty(state, responder);
  if (level === 'easy') return true;
  const uid = state.chainTriggerUid;
  if (!uid) return true;
  const found = findCard(state, uid);
  if (!found || found.location.zone !== 'monsters' || !found.instance.faceUp) {
    return true;
  }
  const summoned = found.instance;
  const atk = getModifiedAtk(summoned, library, state.reverseStatMods);
  if (getLevel(summoned, library) >= 5) return true;
  if (level === 'normal') {
    if (atk >= TRAP_HOLE_NORMAL_ATK) return true;
  } else if (atk >= TRAP_HOLE_PREMIUM_ATK) {
    return true;
  }
  return trapHoleClearsDisadvantage(state, library, responder, atk, level);
}

function trapHoleClearsDisadvantage(
  state: MatchState,
  library: CardLibrary,
  responder: Seat,
  atk: number,
  level: AiDeckDifficulty,
): boolean {
  const ours = getMonsters(state.zones[responder]);
  const faceUp = ours.filter((m) => m.faceUp);
  const reverse = state.reverseStatMods;
  if (faceUp.some((m) => getModifiedAtk(m, library, reverse) > atk)) return false;

  const lp = state.zones[responder].lifePoints;
  let damage = 0;
  let killsBeater = false;
  if (ours.length === 0) {
    damage = atk;
  } else {
    for (const monster of faceUp) {
      const defending = monster.position === CardPosition.DEFENSE;
      const bar = defending
        ? getModifiedDef(monster, library, reverse)
        : getModifiedAtk(monster, library, reverse);
      const body = getModifiedAtk(monster, library, reverse);
      if (atk > bar) {
        if (!defending) damage = Math.max(damage, atk - bar);
        if (body >= BEATER_ATK) killsBeater = true;
      } else if (!defending && atk === bar && body >= BEATER_ATK) {
        killsBeater = true;
      }
    }
  }
  if (killsBeater) return true;
  if (damage >= lp) return true;
  if (damage <= 0) return false;
  const line = level === 'normal' ? 1000 : 2000;
  if (damage >= line) return true;
  if (lp - damage <= WABOKU_LP_FLOOR) return true;
  return false;
}

function planReverseTrap(
  state: MatchState,
  library: CardLibrary,
  responder: Seat,
  pending: PendingChain,
): boolean {
  if (state.reverseStatMods || pending.reverse) return false;
  const fight = currentFight(state, library, responder, pending);
  if (!fight || fight.direct || !fight.defender || !fight.defenderPublic) {
    return false;
  }
  const covered = battleCovered(state, responder, pending);
  const now = simulateBattle(
    fight.attackerAtk, fight.defenderValue, fight.defending,
  );
  const flipped = simulateBattle(
    getModifiedAtk(fight.attacker, library, true),
    fight.defending
      ? getModifiedDef(fight.defender, library, true)
      : getModifiedAtk(fight.defender, library, true),
    fight.defending,
  );
  return (
    aiScore(flipped, fight.aiIsAttacker, covered) >
    aiScore(now, fight.aiIsAttacker, covered)
  );
}

function planJustDesserts(
  state: MatchState,
  responder: Seat,
  pending: PendingChain,
): boolean {
  const opp = otherSeat(responder);
  const n = getMonsters(state.zones[opp]).length;
  if (n <= 0) return false;
  // A copy already on the chain is taking the rest of their Life Points.
  const lifeLeft = state.zones[opp].lifePoints - pending.burn;
  if (lifeLeft <= 0) return false;
  const burn = DESSERTS_PER * n;
  if (burn >= lifeLeft) return true;
  return n >= 2;
}

function castleWallsWouldCover(
  state: MatchState,
  library: CardLibrary,
  fight: Fight,
  pending: PendingChain,
): boolean {
  if (!fight.defender || fight.defender.position !== CardPosition.DEFENSE) {
    return false;
  }
  if (boostBlocked(state, library, fight.defender.uid)) return false;
  const atk = fight.attackerAtk;
  const def = fight.defenderValue;
  const newDef = boostedStat(
    fight.defender,
    library,
    state,
    0,
    BOOST_DEF + (pending.defBoost.get(fight.defender.uid) ?? 0),
  );
  return atk > def && atk <= newDef;
}

interface Fight {
  attackSeat: Seat;
  aiIsAttacker: boolean;
  attacker: CardInstance;
  defender: CardInstance | null;
  /** Face-up now, or already flipped in the Damage Step — legal boost target. */
  defenderFaceUp: boolean;
  /** Stats are known: face-up, already flipped, or our own Set. */
  defenderPublic: boolean;
  defending: boolean;
  direct: boolean;
  attackerAtk: number;
  defenderValue: number;
}

/** Stats as they will stand once the chain resolves (pending boosts included). */
function currentFight(
  state: MatchState,
  library: CardLibrary,
  responder: Seat,
  pending: PendingChain,
): Fight | null {
  const attack = state.pendingAttack;
  if (!attack) return null;
  const attacker = monsterOf(state, attack.attackerUid);
  if (!attacker) return null;
  const reverse = state.reverseStatMods;
  const sign = reverse ? -1 : 1;
  const attackerAtk = Math.max(
    0,
    getModifiedAtk(attacker, library, reverse) +
      sign * (pending.atkBoost.get(attacker.uid) ?? 0),
  );
  const aiIsAttacker = attack.seat === responder;
  if (!attack.targetUid) {
    return {
      attackSeat: attack.seat,
      aiIsAttacker,
      attacker,
      defender: null,
      defenderFaceUp: true,
      defenderPublic: true,
      defending: false,
      direct: true,
      attackerAtk,
      defenderValue: 0,
    };
  }
  const defender = monsterOf(state, attack.targetUid);
  if (!defender) return null;
  const defending = defender.position === CardPosition.DEFENSE;
  const owner = findSeat(state, defender.uid);
  const defenderFaceUp = defender.faceUp || attack.stage === 'preDamage';
  const defenderPublic = defenderFaceUp || owner === responder;
  const defenderBase = defending
    ? getModifiedDef(defender, library, reverse)
    : getModifiedAtk(defender, library, reverse);
  const defenderPending = defending
    ? pending.defBoost.get(defender.uid) ?? 0
    : pending.atkBoost.get(defender.uid) ?? 0;
  const defenderValue = Math.max(0, defenderBase + sign * defenderPending);
  return {
    attackSeat: attack.seat,
    aiIsAttacker,
    attacker,
    defender,
    defenderFaceUp,
    defenderPublic,
    defending,
    direct: false,
    attackerAtk,
    defenderValue,
  };
}

function simulateBattle(
  atk: number,
  targetValue: number,
  defending: boolean,
): BattleResult {
  if (defending) {
    if (atk > targetValue) {
      return {
        attackerDestroyed: false,
        defenderDestroyed: true,
        damageToAttacker: 0,
        damageToDefender: 0,
      };
    }
    if (atk < targetValue) {
      return {
        attackerDestroyed: false,
        defenderDestroyed: false,
        damageToAttacker: targetValue - atk,
        damageToDefender: 0,
      };
    }
    return {
      attackerDestroyed: false,
      defenderDestroyed: false,
      damageToAttacker: 0,
      damageToDefender: 0,
    };
  }
  if (atk > targetValue) {
    return {
      attackerDestroyed: false,
      defenderDestroyed: true,
      damageToAttacker: 0,
      damageToDefender: atk - targetValue,
    };
  }
  if (atk < targetValue) {
    return {
      attackerDestroyed: true,
      defenderDestroyed: false,
      damageToAttacker: targetValue - atk,
      damageToDefender: 0,
    };
  }
  return {
    attackerDestroyed: true,
    defenderDestroyed: true,
    damageToAttacker: 0,
    damageToDefender: 0,
  };
}

/**
 * `covered` is Waboku on our side: our monster cannot die and we take no damage,
 * so only what the boost does to them is still worth a card.
 */
function aiScore(
  result: BattleResult,
  aiIsAttacker: boolean,
  covered = false,
): number {
  const dies = aiIsAttacker ? result.attackerDestroyed : result.defenderDestroyed;
  const weDie = dies && !covered;
  const theyDie = aiIsAttacker ? result.defenderDestroyed : result.attackerDestroyed;
  const ours = aiIsAttacker ? result.damageToAttacker : result.damageToDefender;
  const dmgToUs = covered ? 0 : ours;
  const dmgToThem = aiIsAttacker ? result.damageToDefender : result.damageToAttacker;
  let score = 0;
  if (weDie) score -= 10_000;
  if (theyDie) score += 5_000;
  score -= dmgToUs;
  score += dmgToThem;
  return score;
}

function boostedStat(
  card: CardInstance,
  library: CardLibrary,
  state: MatchState,
  extraAtk: number,
  extraDef: number,
): number {
  const data = getCardData(library, card.cardId);
  const bonus = effectiveStatBonus(card, state.reverseStatMods);
  const sign = state.reverseStatMods ? -1 : 1;
  if (extraDef !== 0) {
    return Math.max(0, (data.def ?? 0) + bonus.def + sign * extraDef);
  }
  return Math.max(0, (data.atk ?? 0) + bonus.atk + sign * extraAtk);
}

function boostBlocked(
  state: MatchState,
  library: CardLibrary,
  uid: string,
): boolean {
  if (!lordOfDFaceUp(state)) return false;
  const found = findCard(state, uid);
  if (!found || found.location.zone !== 'monsters') return false;
  return getCardData(library, found.instance.cardId).race === 'Dragon';
}

function lordOfDFaceUp(state: MatchState): boolean {
  return ([0, 1] as Seat[]).some((s) =>
    getMonsters(state.zones[s]).some(
      (m) => m.faceUp && Number(m.cardId) === STARTER_IDS.LORD_OF_D,
    ),
  );
}

function monsterOf(state: MatchState, uid: string): CardInstance | null {
  const found = findCard(state, uid);
  if (!found || found.location.zone !== 'monsters') return null;
  return found.instance;
}

function findSeat(state: MatchState, uid: string): Seat | null {
  const found = findCard(state, uid);
  return found ? found.location.seat : null;
}
